import DialogContent from '@mui/material/DialogContent'
import { DataEditContextLanguageField } from 'Components/reusable/DataContext/fields/DataEditContextLanguageField'
import { DataEditContextLanguageLevelField } from 'Components/reusable/DataContext/fields/DataEditContextLanguageLevelField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import Grid from '@mui/material/Grid2'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ILanguageOption } from 'Components/reusable/InputFields/LanguagePicker'
import { ILanguageSkill } from 'types/cvsInterfaces'
import { LanguageCode } from 'types/ids'
import { LanguagesData } from 'api/UtilsAPI'
import AddToArrayButton from 'Components/reusable/Buttons/AddToArrayButton'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import NewLanguageItem from './NewLanguageItem'

/** @notExported */
interface LanguageModalProps {
  /** Language skill item. */
  item: ILanguageSkill | null
  /** Language options. */
  languages: LanguagesData
  /** Already added language skills. */
  items: ILanguageSkill[]
  /** Multiple added language skills. */
  multipleItems: ILanguageSkill[]
  /** Function for adding multiple language skills. */
  addMultiple: () => void
  /** Function for removing language skills from array. */
  removeFromArray: (items: ILanguageSkill[]) => void
}

/**
 * Language skill modal.
 *
 * @returns Language skill modal component.
 * @notExported
 */
const LanguageModal: React.FC<LanguageModalProps> = ({
  item,
  items,
  languages,
  multipleItems,
  addMultiple,
  removeFromArray,
}) => {
  const { t } = useTranslation()

  const arrayLanguagesList = useMemo(() => {
    return Object.entries(languages).map(([code, label]) => ({
      languageCode: code,
      label,
    }))
  }, [languages])

  const languagesList = useMemo(() => {
    return Object.entries(languages)
      .filter(([code]) => {
        const isUsedInItems = items.some(
          i => i.languageCode === code && (!item || i.languageCode !== item.languageCode)
        )
        const isUsedInMultiple = multipleItems.some(i => i.languageCode === code)
        return !isUsedInItems && !isUsedInMultiple
      })
      .map(([code, label]) => ({ languageCode: code, label }))
  }, [languages, items, multipleItems, item])

  const handleLanguageSkillDelete = (languageCode: LanguageCode) => {
    const newSkillsData = multipleItems.filter(item => item.languageCode !== languageCode)
    removeFromArray(newSkillsData)
  }

  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
      <DialogContent>
        <Grid container>
          <Grid size={12}>
            <DataEditContextLanguageField<ILanguageSkill>
              field="languageCode"
              languageOptions={languagesList as ILanguageOption[]}
              required
              insetLabel
            />
          </Grid>
          <Grid size={12}>
            <DataEditContextLanguageLevelField<ILanguageSkill> field="level" required fullWidth insetLabel />
          </Grid>
          {!item && (
            <DataEditContextValidation>
              {({ valid }) => (
                <AddToArrayButton valid={valid} clickAction={() => addMultiple()} label={t('language.addLanguage')} />
              )}
            </DataEditContextValidation>
          )}
        </Grid>
      </DialogContent>
      {multipleItems.length > 0 ? (
        <Grid sx={{ minWidth: '150px', paddingTop: '20px', paddingRight: '10px' }}>
          {multipleItems.map((skill, i) =>
            !item ? (
              <NewLanguageItem
                key={skill.id || `new-${i}`}
                skill={skill as ILanguageSkill}
                onDeleteClick={() => handleLanguageSkillDelete(skill.languageCode)}
                languages={arrayLanguagesList}
              />
            ) : null
          )}
        </Grid>
      ) : null}
    </Grid>
  )
}

export default newWithEditModal(LanguageModal)
